/**
 * Utility for managing temporary image storage in memory
 * This allows us to delay actual uploads to Supabase until the form is submitted
 */

// In-memory storage for temporary images
const tempImageStorage = new Map<string, Blob>();

/**
 * Store a temporary image blob in memory
 * @param id A unique identifier for the image (e.g., "option-0", "option-1")
 * @param blob The image blob to store
 * @returns A temporary URL for the image
 */
export function storeTempImage(id: string, blob: Blob): string {
  // Store the blob in memory
  tempImageStorage.set(id, blob);
  
  // Create a temporary URL for the image
  const url = URL.createObjectURL(blob);
  
  return url;
}

/**
 * Retrieve a temporary image blob from memory
 * @param id The unique identifier for the image
 * @returns The image blob, or null if not found
 */
export function getTempImage(id: string): Blob | null {
  return tempImageStorage.get(id) || null;
}

/**
 * Check if a URL is a temporary object URL
 * @param url The URL to check
 * @returns True if the URL is a temporary object URL
 */
export function isTempImageUrl(url: string): boolean {
  return url.startsWith('blob:');
}

/**
 * Clear all temporary images from memory
 */
export function clearTempImages(): void {
  // Revoke all object URLs to prevent memory leaks
  tempImageStorage.forEach((_, id) => {
    const tempUrl = URL.createObjectURL(tempImageStorage.get(id)!);
    URL.revokeObjectURL(tempUrl);
  });
  
  // Clear the storage
  tempImageStorage.clear();
}

/**
 * Clear a specific temporary image from memory
 * @param id The unique identifier for the image
 */
export function clearTempImage(id: string): void {
  if (tempImageStorage.has(id)) {
    const blob = tempImageStorage.get(id)!;
    const tempUrl = URL.createObjectURL(blob);
    URL.revokeObjectURL(tempUrl);
    tempImageStorage.delete(id);
  }
}